import { ArrowDownOutlined } from "@ant-design/icons"
import { Button, Divider, Empty, Flex, Skeleton, Typography } from "antd"
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react"
import { useTranslation } from "react-i18next"

import { firstUnreadIndex } from "../helpers/conversation-labels.ts"
import { dayDivider } from "../helpers/format-timestamp.ts"
import type {
  Message,
  MessageAttachment,
  Participant,
  PendingMessage
} from "../types/messaging.types.ts"
import { MessageRow, PendingMessageRow } from "./MessageRow.tsx"

// Two messages from the same person within this many minutes read as one
// block: same column, no repeated avatar or name.
const GROUPING_MINUTES = 5

const isGrouped = (rows: Message[], index: number): boolean => {
  const previous = rows[index - 1]
  const current = rows[index]
  if (!previous || previous.sender?.id !== current.sender?.id) return false
  if (previous.is_deleted || current.is_deleted) return false
  const gap =
    new Date(current.created).getTime() - new Date(previous.created).getTime()
  return gap < GROUPING_MINUTES * 60_000
}

// How close to the bottom still counts as "reading the latest", so a couple of
// pixels of inertia does not switch the view into catch-up mode.
const BOTTOM_TOLERANCE_PX = 80

// How much of a conversation has to be in the viewport before reaching its
// bottom counts as having read it. Half: a panel clipped to a sliver at the
// edge of the screen is not one somebody is reading.
const VISIBLE_ENOUGH = 0.5

/**
 * Is this conversation actually on screen?
 *
 * Measured rather than observed. An `IntersectionObserver` answers a frame
 * late, and the read effect runs before that first answer arrives — which is
 * precisely how opening the portal's Messages page, where every project's
 * conversation is mounted at once, marked all of them read in the frame before
 * the observer could say which one the reader was looking at.
 *
 * An empty box means nothing has been laid out (jsdom, a hidden ancestor).
 * There is nothing to judge, so the read is not withheld and a single-panel
 * screen behaves exactly as it always has.
 */
const isOnScreen = (node: HTMLElement): boolean => {
  const box = node.getBoundingClientRect()
  if (box.height === 0) return true
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight || 0
  if (!viewportHeight) return true
  const visible = Math.min(box.bottom, viewportHeight) - Math.max(box.top, 0)
  return visible / box.height >= VISIBLE_ENOUGH
}

interface MessageHistoryProps {
  messages: Message[]
  pending: PendingMessage[]
  participants: Participant[]
  currentUserId: string | undefined
  unreadCount: number
  loading: boolean
  loadingOlder?: boolean
  error: boolean
  hasMore: boolean
  searchTerm?: string
  highlightedMessageId: string | null
  canSaveToProjectFiles: boolean
  portal?: boolean
  onLoadOlder: () => void
  onRetryLoad: () => void
  onReply: (message: Message) => void
  onEdit: (message: Message, body: string) => void
  onDelete: (message: Message) => void
  onJumpToMessage: (messageId: string) => void
  onSaveToProjectFiles: (attachment: MessageAttachment) => void
  onRetrySend: (pending: PendingMessage) => void
  onDiscardSend: (clientToken: string) => void
  /** Called with the newest message the reader has actually reached. */
  onReachedMessage: (message: Message) => void
}

export function MessageHistory({
  messages,
  pending,
  participants,
  currentUserId,
  unreadCount,
  loading,
  loadingOlder = false,
  error,
  hasMore,
  searchTerm,
  highlightedMessageId,
  canSaveToProjectFiles,
  portal,
  onLoadOlder,
  onRetryLoad,
  onReply,
  onEdit,
  onDelete,
  onJumpToMessage,
  onSaveToProjectFiles,
  onRetrySend,
  onDiscardSend,
  onReachedMessage
}: MessageHistoryProps) {
  const { t } = useTranslation()
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [onScreen, setOnScreen] = useState(true)
  const [atBottom, setAtBottom] = useState(true)
  const [newBelow, setNewBelow] = useState(0)
  const newestId = messages[messages.length - 1]?.id
  const oldestId = messages[0]?.id
  const lastNewestRef = useRef(newestId)
  const lastOldestRef = useRef(oldestId)
  const lastScrollHeightRef = useRef(0)

  const isAtBottom = () => {
    const node = scrollRef.current
    if (!node) return true
    return (
      node.scrollHeight - node.scrollTop - node.clientHeight <
      BOTTOM_TOLERANCE_PX
    )
  }

  // A new message must never yank the view away from someone reading older
  // history — it offers a button instead, and only auto-scrolls when they were
  // already at the bottom.
  useLayoutEffect(() => {
    const node = scrollRef.current
    const arrivedBelow = newestId !== lastNewestRef.current
    const loadedAbove =
      !arrivedBelow && oldestId !== lastOldestRef.current
    lastNewestRef.current = newestId
    lastOldestRef.current = oldestId
    const previousHeight = lastScrollHeightRef.current
    lastScrollHeightRef.current = node?.scrollHeight ?? 0

    // Earlier history went in above the reader: hold what they were looking
    // at in place rather than leaving them at the top, where the next scroll
    // event would page again.
    if (loadedAbove && node && previousHeight) {
      node.scrollTop += node.scrollHeight - previousHeight
      return
    }
    if (!arrivedBelow || !newestId) return
    if (atBottom) {
      node?.scrollTo({ top: node.scrollHeight })
    } else {
      setNewBelow((count) => count + 1)
    }
  }, [newestId, oldestId, atBottom])

  // The portal stacks every project's conversation on one page, so "the
  // reader is at the bottom" is true of all of them the moment the page
  // mounts — and reading one project silently cleared the rest. A conversation
  // has to be on screen before its position counts as read.
  const measureOnScreen = useCallback(() => {
    const node = scrollRef.current
    if (node) setOnScreen(isOnScreen(node))
  }, [])

  // A callback ref rather than a mount effect: the first render of a loading
  // conversation is a skeleton that never attaches this node, so an effect
  // keyed to mount measured nothing and every panel kept its default.
  const attachScrollport = useCallback(
    (node: HTMLDivElement | null) => {
      scrollRef.current = node
      if (node) setOnScreen(isOnScreen(node))
    },
    []
  )

  // `capture` because a scroll event does not bubble: the portal scrolls an
  // inner container, and a listener on the window would never hear it.
  useEffect(() => {
    window.addEventListener("scroll", measureOnScreen, true)
    window.addEventListener("resize", measureOnScreen)
    return () => {
      window.removeEventListener("scroll", measureOnScreen, true)
      window.removeEventListener("resize", measureOnScreen)
    }
  }, [measureOnScreen])


  useEffect(() => {
    if (!highlightedMessageId) return
    document
      .getElementById(`message-${highlightedMessageId}`)
      ?.scrollIntoView({ block: "center", behavior: "smooth" })
  }, [highlightedMessageId])

  // Reaching the bottom is what marks messages read — delivery alone never
  // does, which is the whole point of reporting a position rather than
  // inferring one from the fetch.
  useEffect(() => {
    if (!atBottom || !onScreen) return
    const newest = messages[messages.length - 1]
    if (newest) onReachedMessage(newest)
  }, [atBottom, onScreen, messages, onReachedMessage])

  // Coming back to the tab is the moment these messages are actually seen.
  // Without this the read never retries: the effect above depends on the
  // message list and the scroll, and neither changes just because the tab
  // regained focus.
  //
  // It lives here rather than beside the conversation because it has to carry
  // the same two conditions — at the bottom, and on screen. Keyed to focus
  // alone it fired for every conversation the portal had mounted, so returning
  // to the tab cleared projects the reader had never scrolled to.
  useEffect(() => {
    const reportWhenVisible = () => {
      if (document.visibilityState !== "visible") return
      if (!atBottom || !onScreen) return
      const newest = messages[messages.length - 1]
      if (newest) onReachedMessage(newest)
    }
    document.addEventListener("visibilitychange", reportWhenVisible)
    window.addEventListener("focus", reportWhenVisible)
    return () => {
      document.removeEventListener("visibilitychange", reportWhenVisible)
      window.removeEventListener("focus", reportWhenVisible)
    }
  }, [atBottom, onScreen, messages, onReachedMessage])

  const handleScroll = () => {
    const node = scrollRef.current
    if (!node) return
    setOnScreen(isOnScreen(node))
    const bottom = isAtBottom()
    setAtBottom(bottom)
    if (bottom) setNewBelow(0)
    if (node.scrollTop < 40 && hasMore && !loading && !loadingOlder) {
      onLoadOlder()
    }
  }

  const jumpToBottom = () => {
    const node = scrollRef.current
    if (!node) return
    node.scrollTo({ top: node.scrollHeight, behavior: "smooth" })
    setNewBelow(0)
  }

  const unreadStart = firstUnreadIndex(
    messages.map((row) => row.id),
    unreadCount
  )

  if (loading && messages.length === 0) {
    return (
      <div className={"flex-1 space-y-4 overflow-y-auto p-4"}>
        <Skeleton avatar active paragraph={{ rows: 2 }} />
        <Skeleton avatar active paragraph={{ rows: 1 }} />
        <Skeleton avatar active paragraph={{ rows: 3 }} />
      </div>
    )
  }

  if (error) {
    return (
      <Flex vertical align={"center"} justify={"center"} gap={"small"} flex={1}>
        <Typography.Text strong>
          {t("messages.errors.historyTitle", "Unable to load this conversation")}
        </Typography.Text>
        <Typography.Text type={"secondary"} className={"max-w-sm text-center"}>
          {t(
            "messages.errors.historyBody",
            "Something went wrong while fetching the messages. Please check your connection and try again."
          )}
        </Typography.Text>
        <Button onClick={onRetryLoad}>{t("common.retry", "Retry")}</Button>
      </Flex>
    )
  }

  let lastDay = ""

  return (
    <div className={"relative min-h-0 min-w-0 flex-1"}>
      <div
        ref={attachScrollport}
        onScroll={handleScroll}
        data-testid={"message-history"}
        // Readable from a test and from the browser: whether this conversation
        // is the one on screen is what decides if reaching its bottom counts
        // as reading it.
        data-on-screen={onScreen ? "true" : "false"}
        className={"h-full overflow-y-auto pb-3"}
      >
        {/* The scroller keeps the panel's full width so its scrollbar sits at
            the panel edge; the reading column inside is capped and centred,
            which is what stops a two-word message from sitting a monitor away
            from the next one. The cap holds when expanded too — expanding buys
            height and a wider list, not a longer line to read. */}
        <div className={"mx-auto w-full max-w-[900px]"}>
          {hasMore && (
            <Flex justify={"center"} className={"py-2"}>
              <Button
                size={"small"}
                type={"link"}
                loading={loadingOlder}
                onClick={onLoadOlder}
              >
                {t("messages.loadOlder", "Load earlier messages")}
              </Button>
            </Flex>
          )}

          {messages.length === 0 && pending.length === 0 && (
            <Empty
              className={"py-16"}
              description={t(
                "messages.emptyConversation",
                "No messages yet. Say hello."
              )}
            />
          )}

          {messages.map((message, index) => {
            const day = dayDivider(message.created)
            const showDay = day !== lastDay
            lastDay = day
            return (
              <React.Fragment key={message.id}>
                {showDay && (
                  <Divider plain className={"!my-3 !text-[11px] !text-slate-400"}>
                    {day}
                  </Divider>
                )}
                {index === unreadStart && (
                  <Divider
                    plain
                    data-testid={"unread-divider"}
                    className={"!my-2 text-xs !text-primary-strong !border-primary/30"}
                  >
                    {t("messages.unreadDivider", {
                      count: unreadCount,
                      defaultValue_one: "{{count}} unread message",
                      defaultValue_other: "{{count}} unread messages"
                    })}
                  </Divider>
                )}
                <MessageRow
                portal={portal}
                  message={message}
                  participants={participants}
                  isOwn={message.sender?.id === currentUserId}
                  grouped={isGrouped(messages, index)}
                  isHighlighted={message.id === highlightedMessageId}
                  searchTerm={searchTerm}
                  canSaveToProjectFiles={canSaveToProjectFiles}
                  onReply={onReply}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onJumpToMessage={onJumpToMessage}
                  onSaveToProjectFiles={onSaveToProjectFiles}
                />
              </React.Fragment>
            )
          })}

          {pending.map((row) => (
            <PendingMessageRow
              key={row.client_token}
              pending={row}
              participants={participants}
              onRetry={onRetrySend}
              onDiscard={onDiscardSend}
            />
          ))}
        </div>
      </div>

      {newBelow > 0 && (
        <Button
          size={"small"}
          type={"primary"}
          icon={<ArrowDownOutlined />}
          onClick={jumpToBottom}
          data-testid={"new-messages-button"}
          className={"absolute bottom-4 left-1/2 -translate-x-1/2 shadow-md"}
        >
          {t("messages.newMessagesBelow", "New messages")}
        </Button>
      )}
    </div>
  )
}
